import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { curriculumData } from '../data/curriculum';
import { useTheme } from '../context/ThemeContext';

const CARTO_API_KEY = process.env.REACT_APP_CARTO_API_KEY;

function cartoTileUrl(isDark) {
  const base = `https://{s}.basemaps.cartocdn.com/${isDark ? 'dark_all' : 'light_all'}/{z}/{x}/{y}{r}.png`;
  return CARTO_API_KEY ? `${base}?key=${CARTO_API_KEY}` : base;
}

function FadeIn({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('visible'); obs.disconnect(); }
    }, { threshold: 0.06 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`fade-in ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

function CVFloatingButton() {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="cv-fab"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div className={`cv-fab-note ${open ? 'cv-fab-note--open' : ''}`} role="note">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"
          style={{ flexShrink: 0, marginTop: 2 }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <span>
          This CV may not reflect my most recent work. Some papers, research projects,
          and other contributions are still under peer review or covered by
          confidentiality agreements, so they cannot yet be listed here.
        </span>
      </div>
      <a
        href="/cv.pdf"
        className="btn-primary cv-fab-btn"
        download="Curriculum_Vitae.pdf"
        aria-label="Download CV"
        onClick={() => setOpen(o => !o)}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Download CV
        <button
          type="button"
          className="cv-fab-info"
          aria-label="Why this CV may be outdated"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </button>
      </a>
    </div>
  );
}

function TypeBadge({ type }) {
  const colors = {
    Education: 'var(--accent)',
    Experience: '#3B82F6',
    Award: '#F59E0B',
    Publication: '#60A5FA'
  };
  return (
    <span className="type-badge" style={{ '--badge-color': colors[type] || 'rgba(255,255,255,0.3)' }}>
      {type}
    </span>
  );
}

function MilestoneCard({ m, isActive, onClick }) {
  return (
    <div
      className={`milestone-card ${isActive ? 'milestone-card--active' : ''}`}
      onClick={onClick}
    >
      <div className="milestone-header">
        <TypeBadge type={m.type} />
        <div className="milestone-meta">
          <span className="milestone-date">{m.dateRange || m.date}</span>
          <span className="milestone-loc">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M20 10c0 6-8 13-8 13s-8-7-8-13a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" />
            </svg>
            {m.location}
          </span>
        </div>
      </div>
      <h3 className="milestone-title">{m.title}</h3>
      <p className="milestone-sub">{m.subtitle}</p>
      <ul className="milestone-desc">
        {m.description && m.description.filter(d => d.trim()).map((d, i) => (
          <li key={i}>
            {d.split('**').map((part, idx) =>
              idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
            )}
          </li>
        ))}
      </ul>
      <div className="milestone-tags">
        {(m.tags || []).map(t => <span key={t} className="tag">{t}</span>)}
      </div>
    </div>
  );
}

function LocationTransition({ from, to }) {
  if (!from || !to || from.location === to.location) return null;
  return (
    <div className="location-transition">
      <div className="lt-line" />
      <div className="lt-label">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
        {from.location} → {to.location}
      </div>
      <div className="lt-line" />
    </div>
  );
}

function CurriculumMapFixed({ activeIdx, isMobile }) {
  const { theme } = useTheme();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markerRef = useRef(null);
  const polylineRef = useRef(null);

  const getOffsetCenter = useCallback((latlng, zoom) => {
    const map = mapRef.current;
    if (!map || isMobile) return latlng;
    const panelWidth = Math.min(600, window.innerWidth * 0.46);
    const targetX = panelWidth + (window.innerWidth - panelWidth) * 0.52;
    const screenCenterX = window.innerWidth / 2;
    const offsetPx = targetX - screenCenterX;
    const markerPt = map.project(L.latLng(latlng), zoom);
    const centerPt = L.point(markerPt.x - offsetPx, markerPt.y);
    return map.unproject(centerPt, zoom);
  }, [isMobile]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: false,
      touchZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      tap: false
    });

    const isDark = theme === 'dark';
    const layer = L.tileLayer(
      cartoTileUrl(isDark),
      { subdomains: 'abcd', maxZoom: 19 }
    ).addTo(map);

    L.control.attribution({ position: 'bottomright', prefix: '' }).addTo(map);

    const icon = L.divIcon({
      className: '',
      html: `<div class="map-pin"><div class="map-pin-inner"></div><div class="map-pin-ring"></div></div>`,
      iconSize: [20, 20], iconAnchor: [10, 10]
    });

    const first = curriculumData[0];
    const initZoom = isMobile ? 5 : 12;
    const firstCoords = first.coords ? [first.coords.lat, first.coords.lng] : [0, 0];

    map.setView(firstCoords, initZoom);

    const marker = L.marker(firstCoords, { icon }).addTo(map);
    const polyline = L.polyline([], {
      color: 'rgba(244,63,94,0.55)',
      weight: 2,
      dashArray: '6 8'
    }).addTo(map);

    mapRef.current = map;
    layerRef.current = layer;
    markerRef.current = marker;
    polylineRef.current = polyline;

    setTimeout(() => {
      const offsetCenter = getOffsetCenter(L.latLng(firstCoords), initZoom);
      map.setView(offsetCenter, initZoom, { animate: false });
    }, 150);

    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
      markerRef.current = null;
      polylineRef.current = null;
    };
  }, [isMobile, getOffsetCenter, theme]);

  useEffect(() => {
    if (!mapRef.current || !layerRef.current) return;
    const isDark = theme === 'dark';
    layerRef.current.setUrl(cartoTileUrl(isDark));
  }, [theme]);

  useEffect(() => {
    if (!mapRef.current || activeIdx < 0 || activeIdx >= curriculumData.length) return;
    const m = curriculumData[activeIdx];
    const mCoords = m.coords ? [m.coords.lat, m.coords.lng] : [0, 0];
    const zoom = isMobile ? 6 : 13;

    const offsetCenter = getOffsetCenter(mCoords, zoom);
    mapRef.current.flyTo(offsetCenter, zoom, { duration: 1.3, easeLinearity: 0.22 });
    markerRef.current?.setLatLng(mCoords);

    const visited = curriculumData.slice(0, activeIdx + 1);
    const uniqueCoords = [];
    visited.forEach(ms => {
      const msC = ms.coords ? [ms.coords.lat, ms.coords.lng] : [0, 0];
      const last = uniqueCoords[uniqueCoords.length - 1];
      if (!last || last[0] !== msC[0] || last[1] !== msC[1]) {
        uniqueCoords.push(msC);
      }
    });
    polylineRef.current?.setLatLngs(uniqueCoords);

    if (markerRef.current) {
      markerRef.current.unbindPopup();
      markerRef.current.bindPopup(
        `<div style="font-size:12px;font-weight:600;white-space:nowrap;">${m.location}</div>`,
        { closeButton: false, autoPan: false, className: 'map-popup-wrap', offset: L.point(0, -14) }
      ).openPopup();
    }
  }, [activeIdx, isMobile, getOffsetCenter]);

  const style = isMobile
    ? { position: 'fixed', top: 'var(--nav-h)', left: 0, right: 0, height: '220px', zIndex: 1 }
    : { position: 'fixed', top: 'var(--nav-h)', left: 0, right: 0, bottom: 0, zIndex: 1 };

  return (
    <div style={style}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {isMobile && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 60, zIndex: 2,
          background: 'linear-gradient(to bottom, transparent, var(--bg))',
          pointerEvents: 'none'
        }} />
      )}
    </div>
  );
}

function TimelineContent({ activeIdx, setActiveIdx }) {
  const milestoneRefs = useRef([]);

  useEffect(() => {
    const observers = curriculumData.map((_, i) => {
      const el = milestoneRefs.current[i];
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveIdx(i); },
        { threshold: 0.35, rootMargin: '-60px 0px -35% 0px' }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o?.disconnect());
  }, [setActiveIdx]);

  return (
    <div style={{ paddingBottom: 60 }}>
      {curriculumData.map((m, i) => (
        <div key={m.id || i} ref={el => milestoneRefs.current[i] = el}>
          <FadeIn delay={Math.min(i * 40, 200)}>
            <MilestoneCard
              m={m}
              isActive={activeIdx === i}
              onClick={() => setActiveIdx(i)}
            />
          </FadeIn>
          {i < curriculumData.length - 1 && <LocationTransition from={curriculumData[i]} to={curriculumData[i + 1]} />}
        </div>
      ))}
    </div>
  );
}

function CurriculumListContent() {
  const categories = ['Education', 'Experience', 'Award', 'Publication'];
  return (
    <div style={{ paddingBottom: 60 }}>
      {categories.map(cat => {
        const items = curriculumData.filter(m => m.type === cat);
        if (!items.length) return null;
        return (
          <FadeIn key={cat}>
            <div className="cv-section">
              <h3 className="cv-category">{cat}</h3>
              {items.map((m, i) => (
                <div key={m.id || i} className={`cv-item ${i < items.length - 1 ? 'cv-item--border' : ''}`}>
                  <div className="cv-left">
                    <span className="cv-date">{m.dateRange || m.date}</span>
                    <span className="cv-loc">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M20 10c0 6-8 13-8 13s-8-7-8-13a8 8 0 0 1 16 0Z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      {m.location}
                    </span>
                  </div>
                  <div className="cv-right">
                    <h4 className="cv-title">{m.title}</h4>
                    {m.subtitle && <p className="cv-sub">{m.subtitle}</p>}
                    <ul className="cv-desc">
                      {m.description && m.description.filter(d => d.trim()).map((d, j) => (
                        <li key={j}>
                          {d.split('**').map((part, idx) =>
                            idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
                          )}
                        </li>
                      ))}
                    </ul>
                    <div className="milestone-tags" style={{ marginTop: 10 }}>
                      {(m.tags || []).map(t => <span key={t} className="tag">{t}</span>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        );
      })}
    </div>
  );
}

export default function Curriculum() {
  const { theme } = useTheme();
  const [view, setView] = useState('timeline');
  const [activeIdx, setActiveIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const panelStyle = isMobile
    ? {
      position: 'relative', zIndex: 2,
      paddingTop: `calc(var(--nav-h) + 220px)`,
      width: '100%',
      background: 'var(--bg)'
    }
    : {
      position: 'relative', zIndex: 2,
      paddingTop: `calc(var(--nav-h) + 28px)`,
      width: view === 'timeline' ? 'min(600px, 46vw)' : '100%',
      margin: view === 'timeline' ? '0' : '0 auto',
      maxWidth: view === 'timeline' ? 'unset' : '1140px',
      minHeight: '100vh',
      background: view === 'timeline'
        ? (theme === 'dark'
          ? 'linear-gradient(to right, rgba(8,8,8,0.97) 0%, rgba(8,8,8,0.97) 78%, rgba(8,8,8,0) 100%)'
          : 'linear-gradient(to right, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.97) 78%, rgba(255,255,255,0) 100%)')
        : 'transparent'
    };

  return (
    <>
      <Helmet>
        <title>Curriculum | Salvatore Lo Sardo</title>
        <meta name="description" content="Professional journey of Salvatore Lo Sardo — education, work experience, awards, and research in AI and computer engineering." />
        <meta property="og:title" content="Curriculum | Salvatore Lo Sardo" />
        <meta property="og:description" content="Professional journey — education, work experience, awards, and research in AI and computer engineering." />
        <meta property="og:url" content="https://www.salvatore.losardo.eu/curriculum" />
        <link rel="canonical" href="https://www.salvatore.losardo.eu/curriculum" />
      </Helmet>
      {view === 'timeline' && (
        <CurriculumMapFixed activeIdx={activeIdx} isMobile={isMobile} />
      )}

      <div style={panelStyle}>
        <div style={{ padding: isMobile ? '0 20px' : (view === 'timeline' ? '0 28px 0 20px' : '0 20px') }}>

          <div className="page-hero" style={{ marginBottom: 20 }}>
            <h1 className="page-hero-title">Curriculum</h1>
            <p className="page-hero-sub">My academic and professional journey,<br />spanning across the world</p>
          </div>

          <div className="view-toggle" style={{ marginBottom: 24 }}>
            <button
              className={`view-btn ${view === 'timeline' ? 'view-btn--active' : ''}`}
              onClick={() => setView('timeline')}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
              Timeline
            </button>
            <button
              className={`view-btn ${view === 'curriculum' ? 'view-btn--active' : ''}`}
              onClick={() => setView('curriculum')}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
              </svg>
              Curriculum
            </button>
          </div>

          {view === 'timeline'
            ? <TimelineContent activeIdx={activeIdx} setActiveIdx={setActiveIdx} />
            : <CurriculumListContent />
          }
        </div>
      </div>

      <CVFloatingButton />

      <style>{`
        [data-theme="light"] .map-pin-inner { background: var(--accent); }
        .leaflet-container { background: var(--bg) !important; }
        [data-theme="light"] .leaflet-container { background: #f8f9fa !important; }

        .cv-fab {
          position: fixed;
          right: 24px;
          bottom: 24px;
          z-index: 1200;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
        }
        .cv-fab-btn {
          box-shadow: 0 6px 22px rgba(0,0,0,0.28);
        }
        .cv-fab-info {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-left: 2px;
          padding: 0;
          background: none;
          border: none;
          color: inherit;
          opacity: 0.7;
          cursor: pointer;
        }
        .cv-fab-info:hover { opacity: 1; }
        .cv-fab-note {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          max-width: 300px;
          padding: 12px 14px;
          font-size: 12.5px;
          line-height: 1.5;
          color: var(--text-muted);
          background: var(--bg);
          border: 1px solid var(--text-muted);
          border-radius: 10px;
          box-shadow: 0 8px 28px rgba(0,0,0,0.3);
          opacity: 0;
          transform: translateY(6px);
          pointer-events: none;
          transition: opacity 0.18s ease, transform 0.18s ease;
        }
        .cv-fab-note--open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
        @media (max-width: 767px) {
          .cv-fab { right: 16px; bottom: 16px; }
          .cv-fab-note { max-width: 240px; }
        }
      `}</style>
    </>
  );
}